// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * The one place the UI reads the alarm ↔ work order link.
 *
 * The backend field wins whenever it is present (`alarm.workOrder`, then
 * `alarm.workOrderId` from AEM, and `workOrder.source_alarm` the other way);
 * when none is set these fall back to what this browser remembered
 * (stores/alarmWorkOrderStore.ts). The store can be deleted once AEM's link is
 * on everywhere.
 */

import { useCallback, useMemo } from 'react';
import type { Alarm } from '../types/alarm';
import type { WorkOrder } from '../types';
import { alarmDetailPath } from '../services/alarmSelectors';
import { selectRaisedWorkOrder, useAlarmWorkOrderStore } from '../stores/alarmWorkOrderStore';

/** A work order's way back to the alarm that raised it. */
export interface AlarmLink {
  /** AEM `alarm_id`. */
  alarmId: string;
  /** The alarm's detail route. */
  path: string;
  /** e.g. "GEN-1-A · Low fuel pressure"; empty when nothing named it. */
  label: string;
}

/**
 * The work order raised for `alarm`: the BFF's, else the one remembered here.
 *
 * `siteId` is the site the order is read and written under. It is null for a
 * legacy order, remembered before work orders belonged to a site: that one was
 * created through the unscoped POST, and only the unscoped routes still find
 * it (a site-scoped read of a siteless order is a 404 upstream).
 */
export function useRaisedWorkOrder(
  alarm: Pick<Alarm, 'alarmId' | 'siteId' | 'workOrder' | 'workOrderId'>
): { id: string; reference: string; siteId: string | null } | null {
  const remembered = useAlarmWorkOrderStore(selectRaisedWorkOrder(alarm.alarmId));
  if (alarm.workOrder) return { ...alarm.workOrder, siteId: alarm.siteId };
  // AEM's link (AEM-06) carries only the id. The reference is this browser's when
  // it raised the same order, else the id stands in until the order is read
  // (the Next step card swaps in the real one once it has it).
  if (alarm.workOrderId) {
    const reference =
      remembered?.id === alarm.workOrderId ? remembered.reference : alarm.workOrderId;
    return { id: alarm.workOrderId, reference, siteId: alarm.siteId };
  }
  if (!remembered) return null;
  return { id: remembered.id, reference: remembered.reference, siteId: remembered.siteId ?? null };
}

/**
 * The alarm-side event still owed on `alarmId`'s work order, if its WOMS write
 * succeeded but the follow-up event was never confirmed applied (see
 * {@link RaisedWorkOrder.pendingEvent}) — null once there is nothing remembered
 * or nothing pending. Only meaningful alongside {@link useRaisedWorkOrder}.
 */
export function usePendingWorkOrderEvent(alarmId: string | undefined): string | null {
  return useAlarmWorkOrderStore(state =>
    alarmId ? (state.byAlarmId[alarmId]?.pendingEvent ?? null) : null
  );
}

/**
 * A lookup from a work order to the alarm that raised it: the BFF's
 * `source_alarm`, else the link remembered here. A function rather than a
 * value so a list can resolve every row from one store subscription.
 *
 * The BFF's link comes from the alarm id the WOMS stores, so it has no rule
 * code or label: the alarm route resolves from `alarm_id` + site alone (see
 * getAlarm), so the id stands in for the code, and a label this browser
 * remembered still names it.
 */
export function useAlarmLinkOf(): (order: WorkOrder) => AlarmLink | undefined {
  const byAlarmId = useAlarmWorkOrderStore(s => s.byAlarmId);
  const remembered = useMemo(() => {
    const map = new Map<string, AlarmLink>();
    for (const [alarmId, entry] of Object.entries(byAlarmId)) {
      if (entry.alarmPath) {
        map.set(entry.id, { alarmId, path: entry.alarmPath, label: entry.alarmLabel ?? '' });
      }
    }
    return map;
  }, [byAlarmId]);

  return useCallback(
    order => {
      const source = order.source_alarm;
      const local = remembered.get(order.id);
      if (!source) return local;
      return {
        alarmId: source.alarm_id,
        path: alarmDetailPath({
          id: source.alarm_code ?? source.alarm_id,
          siteId: source.site_id,
          alarmId: source.alarm_id,
          assetId: source.equipment_id ?? undefined,
        }),
        label: source.label ?? (local?.alarmId === source.alarm_id ? local.label : ''),
      };
    },
    [remembered]
  );
}
